/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br. "Knowledge
 * for Decisions (K4D)" and the slogan "Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

import { useTranslation } from 'react-i18next';
import { DetailTimeline, Text } from '@/shared/ui';
import type { ActivityEntry } from '../utils/lease-detail-derivations';

export interface LeaseActivityTimelineProps {
  entries: ActivityEntry[];
}

/**
 * The "Atividade da locação" column [protótipo, Detalhe de Locação].
 *
 * The entries are reconstructed from the lease's own data rather than read
 * from an events feed, because the backend keeps no audit trail for a
 * lease -- see `buildActivity`. The column therefore shows fewer events
 * than the prototype's mock, and says so when it has nothing to show.
 *
 * @param props.entries - Activity entries, most recent first.
 * @returns The activity column.
 */
export function LeaseActivityTimeline({ entries }: LeaseActivityTimelineProps) {
  const { t } = useTranslation('features/leases');

  return (
    <DetailTimeline
      testId="lease-activity"
      title={t('detail.activity.title')}
      items={entries.map((entry) => ({
        id: entry.id,
        testId: `lease-activity-${entry.id}`,
        title: t(`detail.activity.entries.${entry.titleKey}`),
        detail: entry.detail
          ? t(`detail.activity.details.${entry.titleKey}`, { value: entry.detail })
          : undefined,
        meta: entry.when || undefined,
        // A row the system wrote gets the neutral grey chip and a grey dot;
        // one a person recorded is tinted in the brand seed. Same rule for
        // both, which is what the shared pill already expresses.
        accent: entry.automatic ? undefined : 'var(--seed-base)',
        pill: {
          label: t(entry.automatic ? 'detail.activity.automatic' : 'detail.activity.recorded'),
          accent: entry.automatic ? undefined : 'var(--seed-base)',
        },
      }))}
      empty={
        <Text component="span" className="text-(length:--text-meta) text-(--text-tertiary)">
          {t('detail.activity.empty')}
        </Text>
      }
    />
  );
}

export default LeaseActivityTimeline;
